// StructuralReportGenerator.js — structural "Generate Report".
// Jurisdiction form PDF (filled by the generateStructuralPdf Cloud Function from
// instructions built in structuralForm.js) and the photo report.

import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc, getDocs, collection } from 'firebase/firestore';
import { db } from '../firebaseConfig';
import { useAuth } from '../context/AuthContext';
import Sidebar from '../components/Sidebar';
import Footer from '../components/Footer';
import { generateStructuralPhotoPdf } from '../utils/generatePhotoPdf';
import { getStructuralSections, STRUCTURAL_LEAD, numberStructuralPhotos, sectionIsNA } from '../structuralItems';
import { loadStructural, isConfirmed, structuralProblems } from '../structuralSlots';
import { rosterFromUser, buildSignerProfile, disciplinePaths } from '../disciplines';
import { buildStructuralFill, repairList, resolveDetails } from '../structuralForm';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { app } from '../firebaseConfig';
import '../App.css';

const StructuralReportGenerator = () => {
  const { projectId }   = useParams();
  const { currentUser } = useAuth();
  const navigate        = useNavigate();

  const [project, setProject]         = useState(null);
  const [slots, setSlots]             = useState({});
  const [siteSystems, setSiteSystems] = useState({});
  const [photos, setPhotos]           = useState([]);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading]         = useState(true);
  const [generating, setGenerating]   = useState(false);
  const [done, setDone]               = useState(false);
  const [details, setDetails]         = useState({});
  const [formBusy, setFormBusy]       = useState(false);
  const [formDone, setFormDone]       = useState(false);
  const [fitWarning, setFitWarning]   = useState([]);

  useEffect(() => {
    const load = async () => {
      try {
        const projectDoc = await getDoc(doc(db, 'projects', projectId));
        const proj = projectDoc.exists() ? projectDoc.data() : null;
        setProject(proj);
        const report = await loadStructural(projectId, proj?.county);
        setSlots(report.slots);
        setSiteSystems(report.siteSystems);
        const snap = await getDocs(collection(db, 'projects', projectId, 'photos'));
        setPhotos(snap.docs.map(d => ({ id: d.id, ...d.data() })));
        const formDoc = await getDoc(doc(db, 'projects', projectId, 'forms', 'structural'));
        const signer  = formDoc.exists() ? formDoc.data() : {};
        setDetails(signer.details || {});
        const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
        setUserProfile(buildSignerProfile(rosterFromUser(userDoc.exists() ? userDoc.data() : {}), signer.engineerId, signer.firmId));
      } catch (err) {
        console.error('Error loading structural report data:', err);
      }
      setLoading(false);
    };
    load();
  }, [projectId, currentUser]);

  const sections = useMemo(() => (project ? getStructuralSections(project.county) : []), [project]);
  const allItems = useMemo(() => [...STRUCTURAL_LEAD, ...sections], [sections]);
  const openItems = allItems.filter(it => !isConfirmed(slots[it.key]) || structuralProblems(it, slots[it.key], siteSystems).length > 0);
  const ready = openItems.length === 0;

  const cells       = project ? numberStructuralPhotos(project.county, slots, siteSystems) : [];
  const repairCount = sections.reduce((n, s) => n + (sectionIsNA(s, slots[s.key], siteSystems) ? 0 : (slots[s.key]?.repairs || []).length), 0);
  const naCount     = sections.filter(s => sectionIsNA(s, slots[s.key], siteSystems)).length;
  const paths       = disciplinePaths(projectId, 'structural');
  const repairs     = repairList(slots, sections, siteSystems);
  const resolved    = resolveDetails(details, { project, repairs, condition: details.condition });
  const needsCondition = project && !/broward/i.test(project.county || '') && !resolved.condition;
  const formReady   = ready && userProfile?.hasEngineer && userProfile?.hasFirm && !needsCondition;

  const handleFormPdf = async () => {
    if (!formReady) return;
    setFormBusy(true);
    setFitWarning([]);
    try {
      const { templateKey, ops, missing } = buildStructuralFill({ project, details, slots, siteSystems, profile: userProfile, photos });
      if (missing.length) console.warn('Fields not in template:', missing);
      const call = httpsCallable(getFunctions(app), 'generateStructuralPdf');
      const res = await call({ templateKey, ops });
      if (!res.data?.success) throw new Error('PDF generation failed');
      const bin = atob(res.data.pdf);
      const bytes = new Uint8Array(bin.length);
      for (let i = 0; i < bin.length; i += 1) bytes[i] = bin.charCodeAt(i);
      const url = URL.createObjectURL(new Blob([bytes], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `${project?.address || 'project'}_structural_report.pdf`.replace(/[^a-z0-9.]/gi, '_').toLowerCase();
      a.click();
      URL.revokeObjectURL(url);
      setFitWarning((res.data.overflow || []).map(f => f.replace(/^s/, '').replace(/_\d+$/, '')));
      setFormDone(true);
    } catch (err) {
      console.error('Structural form PDF error:', err);
      alert('Form PDF failed: ' + err.message);
    }
    setFormBusy(false);
  };

  const handlePhotoPdf = async () => {
    if (!ready) return;
    setGenerating(true);
    try {
      const pdf = await generateStructuralPhotoPdf({ project, userProfile, slots, siteSystems, photos });
      pdf.save(`${project?.address || 'project'}_structural_photos.pdf`.replace(/[^a-z0-9.]/gi, '_').toLowerCase());
      setDone(true);
    } catch (err) {
      console.error('Structural photo PDF error:', err);
      alert('Photo report failed: ' + err.message);
    }
    setGenerating(false);
  };

  if (loading) return <div style={{ marginLeft: '220px', padding: '2rem' }}>Loading...</div>;

  return (
    <div>
      <Sidebar />
      <div style={styles.container}>
        <div style={styles.titleRow}>
          <div>
            <h2 style={styles.title}>Structural — Generate Report</h2>
            {project && <p style={styles.subtitle}>{project.address}{project.city ? `, ${project.city}` : ''} — {project.county}</p>}
          </div>
          <button style={styles.backButton} onClick={() => navigate(paths.details)}>← Project Details</button>
        </div>

        <div style={styles.summaryCard}>
          <h3 style={styles.sectionTitle}>Inspection Summary</h3>
          <div style={styles.summaryGrid}>
            <div style={styles.summaryItem}><div style={styles.summaryValue}>{allItems.length - openItems.length} / {allItems.length}</div><div style={styles.summaryLabel}>Cards Confirmed</div></div>
            <div style={styles.summaryItem}><div style={styles.summaryValue}>{cells.length}</div><div style={styles.summaryLabel}>Photos and Panels</div></div>
            <div style={styles.summaryItem}><div style={styles.summaryValue}>{naCount}</div><div style={styles.summaryLabel}>Sections N/A</div></div>
            <div style={styles.summaryItem}><div style={{ ...styles.summaryValue, color: repairCount ? '#b91c1c' : '#065f46' }}>{repairCount}</div><div style={styles.summaryLabel}>Repair Items</div></div>
          </div>
        </div>

        {!ready && (
          <div style={styles.warning}>
            ⚠ {openItems.length} Report Editor card{openItems.length !== 1 ? 's' : ''} not confirmed or needing attention ({openItems.map(it => (it.lead ? it.title : `Sec. ${it.num}`)).join(', ')}). Reports unlock when every card is confirmed.{' '}
            <span style={styles.warningLink} onClick={() => navigate(paths.photos)}>Open Report Editor</span>
          </div>
        )}
        {(!userProfile?.hasEngineer || !userProfile?.hasFirm) && (
          <div style={styles.warning}>
            ⚠ {!userProfile?.hasEngineer && !userProfile?.hasFirm ? 'No engineer or firm selected' : !userProfile?.hasEngineer ? 'No engineer selected' : 'No firm selected'} for the structural report.{' '}
            <span style={styles.warningLink} onClick={() => navigate(paths.details)}>Choose in Project Details</span>
          </div>
        )}
        {userProfile?.hasFirm && !userProfile?.logoUrl && (
          <div style={styles.warning}>
            ⚠ {userProfile.firmName || 'The selected firm'} has no logo. The photo report footer will show the firm name only.{' '}
            <span style={styles.warningLink} onClick={() => navigate('/settings')}>Upload Logo</span>
          </div>
        )}

        <div style={styles.pdfGrid}>
          <div style={styles.pdfCard}>
            <div style={styles.pdfCardIcon}>📋</div>
            <h3 style={styles.pdfCardTitle}>Structural Inspection Report</h3>
            <p style={styles.pdfCardDesc}>
              The completed {project?.county} form: Project Details (building, inspections, supporting data, deterioration, condition), every Report Editor section, and the engineer and firm block.
            </p>
            <div style={styles.pdfCardMeta}>
              <span>Repairs required: {repairs.length ? `Yes (${repairs.length})` : 'No'}</span>
              <span>Engineer: {userProfile?.engineerName || '—'}</span>
              <span>Firm: {userProfile?.firmName || '—'}</span>
              {needsCondition && <span style={{ color: '#b91c1c' }}>Overall condition not selected — set it on Project Details</span>}
            </div>
            {fitWarning.length > 0 && (
              <div style={styles.warning}>
                ⚠ Text did not fit on the form at: {fitWarning.join(', ')}. The PDF shows “…” there. Shorten the text or write “See attached” and attach a sheet.
              </div>
            )}
            <button
              style={{ ...styles.generateButton, backgroundColor: !formReady ? '#9ca3af' : formDone ? '#065f46' : '#112b49', cursor: formReady ? 'pointer' : 'not-allowed' }}
              onClick={handleFormPdf} disabled={formBusy || !formReady}>
              {!ready ? 'Confirm all Report Editor cards first'
                : !(userProfile?.hasEngineer && userProfile?.hasFirm) ? 'Select engineer and firm first'
                : needsCondition ? 'Select the overall condition first'
                : formBusy ? 'Generating...' : formDone ? '✓ Downloaded — Generate Again' : 'Generate Form PDF'}
            </button>
          </div>

          <div style={styles.pdfCard}>
            <div style={styles.pdfCardIcon}>📷</div>
            <h3 style={styles.pdfCardTitle}>Photo Report</h3>
            <p style={styles.pdfCardDesc}>
              Aerial view and building elevations, then each section in form order (N/A sections print a NOT APPLICABLE panel), then the repair photos numbered on from the last section. Four photos per page, captions below. Address header, firm logo footer and page numbers.
            </p>
            <div style={styles.pdfCardMeta}>
              <span>Photos and panels: {cells.length}</span>
              <span>Repair photos: {cells.filter(c => c.repair).length}</span>
              <span>Est. pages: {Math.ceil(cells.length / 4) || 0}</span>
            </div>
            <button
              style={{ ...styles.generateButton, backgroundColor: !ready ? '#9ca3af' : done ? '#065f46' : '#112b49', cursor: ready ? 'pointer' : 'not-allowed' }}
              onClick={handlePhotoPdf} disabled={generating || !ready}>
              {!ready ? 'Confirm all Report Editor cards first' : generating ? 'Generating...' : done ? '✓ Downloaded — Generate Again' : 'Generate Photo PDF'}
            </button>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

const styles = {
  container:     { marginLeft: '200px', padding: '2rem', paddingBottom: '5rem' },
  titleRow:      { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1.5rem' },
  title:         { margin: '0 0 0.25rem', color: '#112b49', fontSize: '1.5rem' },
  subtitle:      { margin: 0, color: '#555', fontSize: '0.9rem' },
  backButton:    { padding: '0.5rem 1.25rem', backgroundColor: '#6b7280', color: 'white', border: 'none', borderRadius: '5px', fontSize: '0.95rem', cursor: 'pointer', fontFamily: 'inherit' },
  summaryCard:   { backgroundColor: 'rgba(255,255,255,0.92)', borderRadius: '8px', padding: '1.5rem', boxShadow: '0 2px 8px rgba(0,0,0,0.1)', marginBottom: '1rem' },
  sectionTitle:  { margin: '0 0 1rem', fontSize: '1rem', fontWeight: '600', color: '#112b49', textTransform: 'uppercase', letterSpacing: '0.05em' },
  summaryGrid:   { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: '1rem' },
  summaryItem:   { textAlign: 'center' },
  summaryValue:  { fontSize: '1.75rem', fontWeight: '700', color: '#112b49', lineHeight: 1, marginBottom: '0.25rem' },
  summaryLabel:  { fontSize: '0.75rem', color: '#6b7280' },
  warning:       { backgroundColor: '#fef9c3', border: '1px solid #fde047', borderRadius: '6px', padding: '0.75rem 1rem', fontSize: '0.875rem', color: '#713f12', marginBottom: '0.75rem' },
  warningLink:   { color: 'cornflowerblue', cursor: 'pointer', fontWeight: '600', textDecoration: 'underline' },
  pdfGrid:       { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '1.25rem', marginTop: '1rem' },
  pdfCard:       { backgroundColor: 'rgba(255,255,255,0.92)', borderRadius: '8px', padding: '1.75rem', boxShadow: '0 2px 8px rgba(0,0,0,0.1)', display: 'flex', flexDirection: 'column', gap: '0.75rem' },
  pdfCardIcon:   { fontSize: '2rem' },
  pdfCardTitle:  { margin: 0, color: '#112b49', fontSize: '1.1rem', fontWeight: '700' },
  pdfCardDesc:   { margin: 0, color: '#555', fontSize: '0.875rem', lineHeight: '1.5' },
  pdfCardMeta:   { display: 'flex', flexDirection: 'column', gap: '0.2rem', fontSize: '0.8rem', color: '#6b7280', backgroundColor: '#f9fafb', padding: '0.75rem', borderRadius: '4px' },
  generateButton:{ padding: '0.75rem', color: 'white', border: 'none', borderRadius: '5px', fontSize: '1rem', fontWeight: '600', cursor: 'pointer', fontFamily: 'inherit', marginTop: 'auto' },
};

export default StructuralReportGenerator;
